import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

/**
 * Emergent Auth callback — runs only when window.location.hash contains "session_id=".
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { completeGoogleLogin } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    if (!sessionId) {
      navigate("/login", { replace: true });
      return;
    }

    // Clear hash immediately so refresh doesn't retrigger
    window.history.replaceState(null, "", window.location.pathname + window.location.search);

    (async () => {
      try {
        const data = await completeGoogleLogin(sessionId);
        if (data.status === "pending") {
          toast.info("Sign-in received — awaiting admin approval.");
          navigate("/login?pending=1", { replace: true });
        } else {
          // Populate AuthContext with the fresh user BEFORE navigating so
          // <Protected> doesn't see `user === null` and bounce to /login.
          toast.success(`Welcome, ${data.name?.split(" ")[0] || data.email}`, { style: { pointerEvents: "none" } });
          navigate("/", { replace: true });
        }
      } catch (err) {
        console.error("[auth] session exchange failed", err);
        setError(err.response?.data?.detail || err.message || "Sign-in failed. Please try again.");
        setTimeout(() => navigate("/login?error=1", { replace: true }), 2500);
      }
    })();
  }, [navigate, completeGoogleLogin]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center text-gray-600" data-testid="auth-callback">
      {error ? (
        <>
          <p className="text-red-600 font-semibold">{error}</p>
          <p className="text-xs text-gray-400 mt-2">Redirecting…</p>
        </>
      ) : (
        <>
          <Loader2 className="animate-spin" size={28} />
          <p className="mt-3 text-sm">Signing you in…</p>
        </>
      )}
    </div>
  );
}
